/**
 * Strategies list (ARCHITECTURE.md §8): the strategy cards (status, dry-run/live
 * guard, mode, realized-P&L sparkline), a status filter, the preset gallery, and
 * import. Server state lives in TanStack Query; the status filter is a typed
 * router search param (`?status=`).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@richrich/ui";
import { useI18n } from "@richrich/i18n/react";
import type { MessageKey } from "@richrich/i18n";
import {
  CurrencySchema,
  StrategyStatusSchema,
  type Currency,
  type StrategyStatus,
} from "@richrich/contracts";
import { PageShell } from "../../components/PageShell";
import { settingsQuery } from "../../lib/query";
import { presetsQuery, strategyListQuery } from "../../features/strategies/api";
import { StrategyCard } from "../../features/strategies/StrategyCard";
import { PresetGallery } from "../../features/strategies/PresetGallery";
import { ImportStrategyPanel } from "../../features/strategies/ImportStrategyPanel";

interface StrategiesSearch {
  status?: StrategyStatus;
}

const BASE_CURRENCY_FALLBACK: Currency = CurrencySchema.parse("KRW");

export const Route = createFileRoute("/strategies/")({
  validateSearch: (search: Record<string, unknown>): StrategiesSearch => {
    const parsed = StrategyStatusSchema.safeParse(search.status);
    return parsed.success ? { status: parsed.data } : {};
  },
  loaderDeps: ({ search }) => ({ status: search.status }),
  loader: async ({ context, deps }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(strategyListQuery(deps.status)),
      context.queryClient.ensureQueryData(presetsQuery()),
    ]);
  },
  component: StrategiesPage,
});

const FILTERS: ReadonlyArray<{ labelKey: MessageKey; value: StrategyStatus | undefined }> = [
  { labelKey: "strategies.filterAll", value: undefined },
  { labelKey: "strategies.statusActive", value: "active" },
  { labelKey: "strategies.statusDraft", value: "draft" },
  { labelKey: "strategies.statusPaused", value: "paused" },
  { labelKey: "strategies.statusHalted", value: "halted" },
  { labelKey: "strategies.statusArchived", value: "archived" },
];

function StrategiesPage() {
  const { t } = useI18n();
  const { status } = Route.useSearch();
  const strategies = useQuery(strategyListQuery(status));
  const settings = useQuery(settingsQuery);
  const baseCurrency = settings.data?.baseCurrency ?? BASE_CURRENCY_FALLBACK;

  const items = strategies.data?.strategies ?? [];

  return (
    <PageShell
      eyebrow={t("strategies.listEyebrow")}
      title={t("strategies.listTitle")}
      description={t("strategies.listDescription")}
      actions={<StatusFilter current={status} />}
    >
      <div className="flex flex-col gap-6">
        <div className="flex justify-end">
          <ImportStrategyPanel />
        </div>

        {strategies.isPending ? (
          <CardsSkeleton />
        ) : strategies.isError ? (
          <p className="py-10 text-center text-sm text-critical">{t("strategies.listLoadError")}</p>
        ) : items.length === 0 ? (
          <EmptyState filtered={status !== undefined} />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((strategy) => (
              <StrategyCard key={strategy.id} strategy={strategy} baseCurrency={baseCurrency} />
            ))}
          </div>
        )}

        <PresetGallery />
      </div>
    </PageShell>
  );
}

function StatusFilter({ current }: { current: StrategyStatus | undefined }) {
  const { t } = useI18n();
  return (
    <div className="inline-flex items-center gap-0.5 rounded-lg border border-hairline bg-page p-0.5">
      {FILTERS.map((filter) => {
        const active = filter.value === current;
        return (
          <Link
            key={filter.value ?? "all"}
            to="/strategies"
            search={filter.value === undefined ? {} : { status: filter.value }}
            className={cn(
              "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
              active ? "bg-surface text-ink shadow-sm" : "text-muted hover:text-ink-secondary",
            )}
          >
            {t(filter.labelKey)}
          </Link>
        );
      })}
    </div>
  );
}

function EmptyState({ filtered }: { filtered: boolean }) {
  const { t } = useI18n();
  return (
    <div className="rounded-xl border border-dashed border-hairline py-14 text-center">
      <p className="text-sm font-medium text-ink">
        {filtered ? t("strategies.emptyFiltered") : t("strategies.emptyTitle")}
      </p>
      <p className="mt-1 text-sm text-muted">{t("strategies.emptyHint")}</p>
    </div>
  );
}

function CardsSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className="h-[168px] animate-pulse rounded-xl border border-hairline bg-surface"
        />
      ))}
    </div>
  );
}
